import React, {startTransition, useState} from "react";
import {useQueryClient} from "react-query";
import {Link, useNavigate} from "react-router-dom";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {alertStateAtom} from "../../Recoil/Atom/AlertAtom";
import {tokenSelector} from "../../Recoil/Atom/authState";
import {useGetQuery} from "../../hooks/useQueryHooks";
import {ResponseList} from "../../models/clientModels/interfaces/Response";
import {
  EQWorkOrder,
  WorkOrderListParams,
  getSerialNumber,
  initializeWorkOrderListParams,
  statusNames,
} from "../../models/serverModels/workOrder/EQWorkOrder";

import * as workOrderApi from "../../api/workOrderApi/workOrderApi";

import {Controller, useForm} from "react-hook-form";

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  styled,
} from "@mui/material";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {ReactComponent as ExcelIcon} from "../../assets/images/excel.svg";
import MWSkeleton from "../../components/MWSkeleton";
import SubTitle from "../../components/SubTitle";
import {useDepartmentList} from "../../hooks/useDepartmentList";
import {typeValue} from "../../models/clientModels/data/selectList";
import {workOrderListTableHead} from "../../models/clientModels/data/tableHeaderData";
import {FilterGroup, SubMenuGroup} from "../../styledComponents/StyledListContainer";
import {PaginationContainer} from "../../styledComponents/StyledPagination";
import {StyledTableContainer, StyledTableHead} from "../../styledComponents/StyledTable";
import ko from "date-fns/locale/ko";

const ListContainer = styled(Box)(({theme}) => ({
  minWidth: "1700px",
}));

const WorkOrderList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const tokenState = useRecoilValue(tokenSelector);
  const setAlertState = useSetRecoilState(alertStateAtom);

  const [page, setPage] = useState<number>(1);

  const [filter, setFilter] = useState<WorkOrderListParams>({
    ...initializeWorkOrderListParams,
    token: tokenState,
  });

  const queryString = Object.entries(filter)
    .map((e) => e.join("="))
    .join("&");

  const {departmentList} = useDepartmentList();

  //note 점검·정비 발신·수신 목록
  const {data, isLoading, isFetching} = useGetQuery<ResponseList<EQWorkOrder>>({
    key: ["WorkOrderList", {...filter, token: tokenState}],
    api: () => workOrderApi.getList({...filter, token: tokenState}),
  });

  const {handleSubmit, control, getValues, watch, reset, setValue} = useForm({});

  const selectChangeHandler = async (event: SelectChangeEvent<any>, name: string) => {
    startTransition(() => {
      setValue(name, event.target?.value);
      setFilter({...filter, page: 1, [event.target.name]: event.target?.value});
    });
  };
  const workOrderFilteringHandler = (
    event: React.BaseSyntheticEvent<Event, EventTarget & HTMLInputElement>
  ) => {
    setValue(event.target.name, event.target.value);
    setFilter({...filter, page: 1, [event.target.name]: event.target.value});
  };

  const startDateHandler = (newValue: Date | null) => {
    setFilter({
      ...filter,
      page: 1,
      startDate: newValue,
    });
  };

  const endDateHandler = (newValue: Date | null) => {
    setFilter({
      ...filter,
      page: 1,
      endDate: newValue,
    });
  };

  const resetHandler = () => {
    setFilter(initializeWorkOrderListParams);
    reset();
  };

  const paginationHandleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setFilter({...filter, page: value});
    setPage(value);
  };

  return (
    <ListContainer>
      <SubTitle subTitle="점검·정비 발신·수신 목록">
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<ExcelIcon width="18px" height="18px" />}
          sx={{marginLeft: "18px"}}
        >
          <a href={"/API/ExportWorkOrdersToExcel?" + queryString} style={{color: "white"}}>
            Excel로 저장
          </a>
        </Button>
      </SubTitle>
      <SubMenuGroup>
        <FilterGroup>
          <Box>
            <Controller
              name="statusIndex"
              control={control}
              defaultValue={-1}
              render={({field}) => (
                <FormControl>
                  <InputLabel id="statusIndex">상태</InputLabel>
                  <Select
                    {...field}
                    labelId="statusIndex"
                    id="statusIndex"
                    label="상태"
                    size="small"
                    onChange={(event) => selectChangeHandler(event, "statusIndex")}
                  >
                    <MenuItem value={-1}>전체</MenuItem>
                    {statusNames.map((item, index) => (
                      <MenuItem key={index} value={index}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>
          <Box>
            <Controller
              name="specificationTypeIndex"
              control={control}
              defaultValue={-1}
              render={({field}) => (
                <FormControl>
                  <InputLabel id="typeLabel">설비분류</InputLabel>
                  <Select
                    {...field}
                    labelId="typeLabel"
                    id="specificationTypeIndex"
                    label="설비분류"
                    size="small"
                    onChange={(event: SelectChangeEvent<any>) =>
                      selectChangeHandler(event, "specificationTypeIndex")
                    }
                  >
                    {typeValue?.map((item) => (
                      <MenuItem key={item.value} value={item?.value!}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>
          <Box>
            <Controller
              name="identifyNo"
              control={control}
              defaultValue=""
              render={({field}) => (
                <TextField
                  {...field}
                  label="설비번호"
                  size="small"
                  onChange={workOrderFilteringHandler}
                />
              )}
            />
          </Box>
          <Box>
            <Controller
              name="departmentUID"
              control={control}
              defaultValue={-1}
              render={({field}) => (
                <FormControl>
                  <InputLabel id="departmentUID">작성부서</InputLabel>
                  <Select
                    {...field}
                    labelId="departmentUID"
                    id="departmentUID"
                    label="작성부서"
                    size="small"
                    onChange={(event) => selectChangeHandler(event, "departmentUID")}
                  >
                    <MenuItem value={-1}>전체</MenuItem>
                    {departmentList?.items.map((item) => (
                      <MenuItem key={item.uid} value={item?.uid}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>
          <Box>
            <Controller
              name="writerName"
              control={control}
              defaultValue=""
              render={({field}) => (
                <TextField
                  {...field}
                  label="작성자"
                  size="small"
                  onChange={workOrderFilteringHandler}
                />
              )}
            />
          </Box>
          <Box>
            <Controller
              name="specificationName"
              control={control}
              defaultValue=""
              render={({field}) => (
                <TextField
                  {...field}
                  label="설비명"
                  size="small"
                  onChange={workOrderFilteringHandler}
                />
              )}
            />
          </Box>
          <Box>
            <Controller
              name="level"
              control={control}
              defaultValue=""
              render={({field}) => (
                <TextField
                  {...field}
                  label="설비등급"
                  size="small"
                  onChange={workOrderFilteringHandler}
                />
              )}
            />
          </Box>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
            <Box>
              <DatePicker
                inputFormat={"yyyy년MM월dd일"}
                label="시작일"
                value={filter.startDate}
                onChange={startDateHandler}
                renderInput={(params) => (
                  <TextField size="small" sx={{fontSize: "10px"}} {...params} />
                )}
              />
            </Box>
            <Box>
              <DatePicker
                inputFormat={"yyyy년MM월dd일"}
                label="종료일"
                value={filter.endDate}
                onChange={endDateHandler}
                renderInput={(params) => <TextField size="small" {...params} />}
              />
            </Box>
          </LocalizationProvider>
          <Button
            onClick={resetHandler}
            variant="contained"
            color="success"
            size="small"
            sx={{whiteSpace: "nowrap"}}
          >
            초기화
          </Button>
        </FilterGroup>
      </SubMenuGroup>
      <StyledTableContainer>
        <Table>
          <StyledTableHead>
            <TableRow>
              {workOrderListTableHead.map((item, index) => (
                <TableCell key={index} align="center">
                  {item}
                </TableCell>
              ))}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {isLoading && isFetching ? (
              <MWSkeleton row={12} column={10} />
            ) : (
              data?.items.map((item, index) => (
                <TableRow key={item.uid}>
                  <TableCell align="center">
                    {page === 1
                      ? index + 1
                      : (page - 1) * initializeWorkOrderListParams.rows! + index + 1}
                  </TableCell>
                  <TableCell align="center">
                    {statusNames.find((v, index) => (index === item.statusIndex ? v : ""))}
                  </TableCell>
                  <TableCell align="center">
                    <Link to={`View/${item.uid}`}>{getSerialNumber(item)}</Link>
                  </TableCell>
                  <TableCell align="center">{item.department}</TableCell>
                  <TableCell align="center">{item.writerName}</TableCell>
                  <TableCell align="center">
                    {typeValue?.map((v) => (v.value === item.specification?.type ? v.label : ""))}
                  </TableCell>
                  <TableCell align="center">{item.specification?.identifyNumber}</TableCell>
                  <TableCell align="center">{item.specification?.name}</TableCell>
                  <TableCell align="left">{item.requestContent}</TableCell>
                  <TableCell align="center">{`${new Date(item?.requestDate).getFullYear()}년 ${
                    new Date(item?.entryDate).getMonth() + 1
                  }월 ${new Date(item?.entryDate).getDate()}일`}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* 페이지네이션 */}
      <PaginationContainer>
        <Pagination
          count={data?.maxPage}
          page={page}
          onChange={paginationHandleChange}
          variant="outlined"
          color="primary"
          shape="rounded"
        />
      </PaginationContainer>
    </ListContainer>
  );
};

export default WorkOrderList;
